/**
 * Background save queue — writes that outlive the component that started them.
 *
 * An edit belongs to the roster the moment the user makes it, not a second and
 * a half later when a debounce expires: switch pane in between and the change
 * leaves with the component that was still holding it. So an edit is handed
 * here instead of being timed. The task lives outside React, so unmounting the
 * popup does not cancel it; it is retried with backoff, so a slow or briefly
 * dropped connection delays the write rather than losing it; and the user can
 * carry on editing — or leave the pane entirely — while it goes.
 *
 * Everything is per key (an officer, a counter — whatever the writes contend
 * over):
 *   - one write in flight at a time, so two edits can never land out of order;
 *   - a write queued while another is running REPLACES any other write already
 *     waiting, because each one carries that subject's whole state and the
 *     newest is simply the truth;
 *   - a failed write is retried until it lands, unless a newer write is already
 *     waiting to supersede it — in which case retrying the stale one is worse
 *     than dropping it.
 */

/** A write. Must post the subject's complete state, so the newest supersedes
 *  every earlier one and retrying is always safe. */
export type SaveTask = () => Promise<unknown>;

interface Slot {
  /** The next write to run — at most one, always the most recent. */
  waiting: SaveTask | null;
  /** Resolves when this key has nothing left to write. */
  drained: Promise<void>;
  running: boolean;
}

/** Backoff between retries. The last value repeats until MAX_ATTEMPTS is spent —
 *  roughly two minutes of trying, which covers a lift, a tunnel or a redeploy. */
const RETRY_DELAYS_MS = [400, 1200, 3000, 8000, 15000];
const MAX_ATTEMPTS = 12;

const slots = new Map<string, Slot>();

let onFailure: ((key: string, error: unknown) => void) | null = null;

/** Report a write that could not be landed after every retry. Set once, at app
 *  level, so the message reaches the user even when the editor is long gone. */
export function setSaveQueueFailureHandler(fn: ((key: string, error: unknown) => void) | null): void {
  onFailure = fn;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Queue `task` as the newest write for `key`, superseding any write still waiting. */
export function queueSave(key: string, task: SaveTask): void {
  const slot = slots.get(key) ?? { waiting: null, drained: Promise.resolve(), running: false };
  slots.set(key, slot);
  slot.waiting = task;
  if (!slot.running) {
    slot.running = true;
    slot.drained = drain(key, slot);
  }
}

async function drain(key: string, slot: Slot): Promise<void> {
  try {
    while (slot.waiting) {
      const task = slot.waiting;
      slot.waiting = null;
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        try {
          await task();
          break;
        } catch (error) {
          // A newer write is already waiting — it carries the same state and
          // more, so retrying this one only delays it.
          if (slot.waiting) break;
          // Refused because the tab is view-only: retrying cannot change that,
          // and the app has already told the user why (see api/client).
          if ((error as Error | null)?.name === 'ViewOnlyError') break;
          if (attempt === MAX_ATTEMPTS - 1) {
            onFailure?.(key, error);
            break;
          }
          await sleep(RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)]);
        }
      }
    }
  } finally {
    slot.running = false;
  }
}

/** Whether anything is still unwritten — for one key, or anywhere. */
export function hasPendingSaves(key?: string): boolean {
  return keyedPending(key) || (key === undefined && orderedPending > 0);
}

/** Whether a keyed save (queueSave) is still unwritten — for one key, or any. */
function keyedPending(key?: string): boolean {
  if (key !== undefined) {
    const slot = slots.get(key);
    return !!slot && (slot.running || slot.waiting !== null);
  }
  for (const slot of slots.values()) {
    if (slot.running || slot.waiting !== null) return true;
  }
  return false;
}

/**
 * Wait until queued writes have landed — for one key, or all of them.
 *
 * For callers that genuinely need the server up to date before their next step
 * (an auto-assign that reads what the user just typed, say). Ordinary edits
 * should not wait: that is the lag this queue exists to remove.
 */
export async function flushSaves(key?: string): Promise<void> {
  // Ordered writes (runInOrder) count too when waiting on everything.
  if (key === undefined) await orderedTail.catch(() => undefined);
  await flushKeyed(key);
}

async function flushKeyed(key?: string): Promise<void> {
  const pending = key !== undefined
    ? [slots.get(key)?.drained]
    : [...slots.values()].map((s) => s.drained);
  await Promise.all(pending.filter(Boolean));
  // A task that finished may have queued another (a save whose success handler
  // writes again); settle those too before calling the queue empty.
  if (keyedPending(key)) await flushKeyed(key);
}

/**
 * Run writes strictly one after another, in the order they were queued — for writes
 * that each carry only a CHANGE (a block move: "these slots, from counter A to B"),
 * which unlike the per-key saves above cannot supersede one another. Two moves sent
 * side by side could reach the server in either order, and the second applied first
 * left the roster where the first one put it: the move that "reverted". Each waits
 * for the keyed saves already queued, too, so a move never overtakes a popup edit
 * made just before it. A failed write rejects its own promise and the chain goes on.
 */
let orderedTail: Promise<unknown> = Promise.resolve();
let orderedPending = 0;
export function runInOrder<T>(task: () => Promise<T>): Promise<T> {
  orderedPending += 1;
  const run = orderedTail
    .catch(() => undefined)
    .then(() => flushKeyed())
    .then(task)
    .finally(() => { orderedPending -= 1; });
  orderedTail = run;
  return run;
}

// A reload with writes still in flight loses them, and the browser is the only
// thing that can ask the user to wait.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event) => {
    if (!hasPendingSaves()) return;
    event.preventDefault();
    event.returnValue = '';
  });
}
